import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Check, ArrowRight, Calculator, CreditCard, Calendar, Sparkles, Star, Users, Crown, Zap, Shield, TrendingUp, DollarSign } from 'lucide-react';
import mockData from '../mock';
import SEOHead from '../components/SEOHead';

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeCard, setActiveCard] = useState(1);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const monthlyTotal = mockData.pricing.monthly.price * 12;
  const yearlyTotal = mockData.pricing.yearly.price;
  const yearlySavings = monthlyTotal - yearlyTotal;

  const pricingPlans = [
    {
      id: 1,
      name: "Starter",
      price: isYearly ? Math.round(25 * 12 * 0.9) : 25, // 10% discount on yearly
      originalPrice: isYearly ? 25 * 12 : 25,
      period: isYearly ? "year" : "month",
      description: "Perfect for small businesses starting with AI",
      features: [
        "Up to 1,000 messages/month",
        "Basic AI training",
        "Email support",
        "Standard integrations",
        "Basic analytics"
      ],
      popular: false,
      color: "from-blue-400 to-cyan-500"
    },
    {
      id: 2,
      name: "Professional",
      price: isYearly ? Math.round(30 * 12 * 0.9) : 30, // 10% discount on yearly
      originalPrice: isYearly ? 30 * 12 : 30,
      period: isYearly ? "year" : "month",
      description: "Most popular choice for growing businesses",
      features: [
        "Up to 1,500 messages/month",
        "Advanced AI training (monthly retraining)",
        "24/7 priority support",
        "WhatsApp integration",
        "Advanced analytics (coming soon...)"
      ],
      popular: true,
      color: "from-violet-400 to-purple-500"
    },
    {
      id: 3,
      name: "Enterprise",
      price: isYearly ? Math.round(60 * 12 * 0.9) : 60, // 10% discount on yearly
      originalPrice: isYearly ? 60 * 12 : 60,
      period: isYearly ? "year" : "month",
      description: "For large organizations with complex needs",
      features: [
        "Up to 3,000 messages/month",
        "Advanced AI training (monthly retraining)",
        "24/7 priority support",
        "WhatsApp, Messenger & Instagram integration",
        "Advanced analytics (coming soon...)",
        "Custom branding",
        "Dedicated account manager"
      ],
      popular: false,
      color: "from-amber-400 to-orange-500"
    }
  ];

  const setupFees = [
    {
      icon: Calculator,
      title: "AI Training & Setup",
      description: "Custom AI model training on your business data",
      price: mockData.pricing.setup.total,
      features: [
        "Data analysis & processing",
        "Custom AI model training",
        "Brand voice customization",
        "Testing & optimization",
        "Integration setup"
      ]
    }
  ];

  const paymentOptions = [
    {
      icon: CreditCard,
      title: "Down Payment",
      amount: mockData.pricing.setup.downPayment,
      description: "Pay now to start your setup",
      highlight: true
    },
    {
      icon: Calendar,
      title: "Final Payment",
      amount: mockData.pricing.setup.remaining,
      description: "Pay after successful delivery",
      highlight: false
    }
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Pricing - BrixBot AI Customer Service Plans" 
        description="Transparent pricing for BrixBot AI chatbots. Start at $25/month with one-time setup. No hidden fees, flexible billing options."
        isIndexable={true}
      />
      {/* Hero Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 hover:bg-violet-600/30 mb-8 text-lg py-2 px-6">
            <DollarSign className="w-5 h-5 mr-2" />
            Transparent Pricing
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            Simple, Transparent
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 animate-pulse">
              No Hidden Fees
            </span>
          </h1>
          
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            One-time setup fee with flexible monthly or yearly billing. No contracts, no surprises.
          </p>
          
          <div className="flex items-center justify-center space-x-6 mb-12">
            <span className={`text-xl ${!isYearly ? 'text-white font-semibold' : 'text-gray-400'}`}>
              Monthly
            </span>
            <div className="relative">
              <Switch
                checked={isYearly}
                onCheckedChange={setIsYearly}
                className="data-[state=checked]:bg-violet-600 scale-125 relative z-10"
              />
              <div className="absolute -inset-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full blur opacity-20 -z-10"></div>
            </div>
            <span className={`text-xl ${isYearly ? 'text-white font-semibold' : 'text-gray-400'}`}>
              Yearly
            </span>
            {isYearly && (
              <Badge className="bg-gradient-to-r from-green-400 to-emerald-500 text-white border-0 animate-pulse">
                Save 10%
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-32 bg-gray-900/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden transition-all duration-500 transform hover:scale-105 ${
                  plan.popular 
                    ? 'bg-gray-900/70 border-violet-500/50 ring-2 ring-violet-400/50 shadow-2xl shadow-violet-500/20' 
                    : 'bg-gray-900/50 border-violet-500/20 hover:border-violet-400/40'
                } backdrop-blur-sm`}
                style={{ 
                  animationDelay: `${index * 200}ms`,
                  transform: `translateY(${Math.sin(scrollY * 0.01 + index) * 10}px) ${activeCard === plan.id ? 'scale(1.05)' : 'scale(1)'}`
                }}
                onMouseEnter={() => setActiveCard(plan.id)}
                onMouseLeave={() => setActiveCard(1)}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
                )}
                
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="text-center relative z-10 py-8">
                  {plan.popular && (
                    <Badge className="bg-gradient-to-r from-violet-600 to-purple-600 text-white border-0 mx-auto mb-4 text-lg py-2 px-4">
                      <Crown className="w-5 h-5 mr-2" />
                      Most Popular
                    </Badge>
                  )}
                  
                  <div className={`w-20 h-20 bg-gradient-to-br ${plan.color} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    {plan.id === 1 && <Zap className="w-10 h-10 text-white" />}
                    {plan.id === 2 && <Star className="w-10 h-10 text-white" />}
                    {plan.id === 3 && <Shield className="w-10 h-10 text-white" />}
                  </div>
                  
                  <CardTitle className="text-2xl text-white mb-2">{plan.name}</CardTitle>
                  
                  {/* Elegant integrated free trial message with neon glow */}
                  <div className="mb-3 px-4 py-2 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-400/30 rounded-lg backdrop-blur-sm relative overflow-hidden animate-[glow_3s_ease-in-out_infinite]">
                    {/* Animated neon border */}
                    <div className="absolute inset-0 rounded-lg">
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-violet-400/20 via-purple-500/30 to-violet-400/20 animate-[borderGlow_2s_ease-in-out_infinite_alternate]"></div>
                      <div className="absolute inset-[1px] rounded-lg border border-violet-400/40 animate-pulse"></div>
                    </div>
                    
                    {/* Flowing light effect */}
                    <div className="absolute inset-0 rounded-lg overflow-hidden">
                      <div className="absolute -top-2 -right-4 w-8 h-8 bg-violet-400/20 rounded-full blur-xl animate-[floatUp_4s_ease-in-out_infinite]"></div>
                      <div className="absolute -bottom-2 -left-4 w-6 h-6 bg-purple-400/20 rounded-full blur-xl animate-[floatUp_3s_ease-in-out_infinite_reverse]"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <p className="text-sm text-violet-300 font-medium animate-[textGlow_2s_ease-in-out_infinite]">
                        ✨ Your first month is on us!
                      </p>
                    </div>
                    
                    {/* Corner sparkles */}
                    <div className="absolute top-1 right-2 w-1 h-1 bg-violet-400 rounded-full animate-pulse opacity-60"></div>
                    <div className="absolute bottom-1 left-2 w-1 h-1 bg-purple-400 rounded-full animate-pulse delay-700 opacity-60"></div>
                    <div className="absolute top-1 left-1/2 w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-1000 opacity-80"></div>
                  </div>
                  
                  <CardDescription className="text-gray-400 text-lg mb-6">
                    {plan.description}
                  </CardDescription>
                  
                  <div className="space-y-2">
                    {isYearly && (
                      <div className="text-lg text-gray-500 line-through">
                        ${plan.originalPrice}/{plan.period}
                      </div>
                    )}
                    <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
                      ${plan.price}
                    </div>
                    <div className="text-gray-400">per {plan.period}</div>
                    {isYearly && (
                      <div className="text-sm text-green-400 font-medium">
                        Save ${plan.originalPrice - plan.price} per year
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="relative z-10 px-8 pb-8">
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className={`w-5 h-5 bg-gradient-to-br ${plan.color} rounded-full flex items-center justify-center`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    className={`w-full py-6 text-lg rounded-2xl transition-all duration-300 transform hover:scale-105 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40' 
                        : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 hover:border-violet-400/40'
                    }`}
                  >
                    {plan.popular ? (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Choose {plan.name}
                      </>
                    ) : (
                      <>Choose {plan.name}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Fee Section */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Calculator className="w-4 h-4 mr-2" />
              One-Time Setup
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Custom AI Training & Setup
            </h2>
            <p className="text-xl text-gray-300">
              Professional setup and training tailored to your business
            </p>
          </div>

          <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
            
            <CardHeader className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-3xl text-white mb-4">Complete Setup Package</CardTitle>
              <CardDescription className="text-gray-300 text-xl">
                Everything you need to get your AI chatbot running perfectly
              </CardDescription>
            </CardHeader>
            
            <CardContent className="px-12 pb-12">
              <div className="text-center mb-12">
                <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400 mb-4">
                  ${mockData.pricing.setup.total}
                </div>
                <div className="text-gray-400 text-xl">
                  Total setup cost
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {paymentOptions.map((option, index) => (
                  <div key={index} className={`p-6 rounded-2xl border ${option.highlight ? 'bg-violet-500/10 border-violet-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${option.highlight ? 'bg-violet-500/20' : 'bg-gray-700'}`}>
                        <option.icon className={`w-6 h-6 ${option.highlight ? 'text-violet-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-lg">{option.title}</div>
                        <div className="text-gray-400 text-sm">{option.description}</div>
                      </div>
                    </div>
                    <div className={`text-3xl font-bold mt-4 ${option.highlight ? 'text-violet-400' : 'text-white'}`}>
                      ${option.amount}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 mb-12">
                <h4 className="text-2xl font-semibold text-white text-center">What's Included:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {setupFees[0].features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-violet-500/20 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-violet-400" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/register" className="block">
                <Button 
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-8 text-2xl rounded-2xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
                >
                  <Sparkles className="w-6 h-6 mr-3" />
                  Start with ${mockData.pricing.setup.downPayment} Down Payment
                  <ArrowRight className="ml-3 w-6 h-6" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-300 text-xl">
              Everything you need to know about BrixBot pricing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockData.faq.map((item, index) => (
              <Card key={item.id} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center">
                    <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-violet-400 font-bold">{index + 1}</span>
                    </div>
                    {item.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/30 to-purple-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-8 text-lg py-2 px-6">
            <TrendingUp className="w-5 h-5 mr-2" />
            Ready to Get Started?
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Transform Your Business with
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
              BrixBot AI Today
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Join businesses already using BrixBot to revolutionize their customer service.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
              >
                <Sparkles className="w-6 h-6 mr-3" />
                Get Started Now
              </Button>
            </Link>
            
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="lg" 
                className="border-violet-500/50 text-violet-300 hover:bg-violet-600/10 hover:border-violet-400 px-12 py-8 text-2xl rounded-3xl backdrop-blur-sm transition-all duration-300"
              >
                <Users className="w-6 h-6 mr-3" />
                Talk to Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;